import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
  CircleF,
} from '@react-google-maps/api';
import Loader from './Loader';

const containerStyle = {
  position: 'relative',
  height: '600px',
};

const hoverDistanceStyle = {
  position: 'absolute',
  top: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '8px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  borderRadius: '4px',
  zIndex: 1000,
  fontSize: '14px',
  pointerEvents: 'none',
};

const formatMeters = meters => {
  if (typeof meters !== 'number' || !Number.isFinite(meters)) return null;
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)}km`;
  }
  return `${Math.round(meters)}m`;
};

const computeMeters = (p1, p2) => {
  if (!p1 || !p2) return null;
  if (!window.google?.maps?.geometry?.spherical) return null;
  return window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
};

const isValidLatLng = point =>
  point &&
  Number.isFinite(Number(point.lat)) &&
  Number.isFinite(Number(point.lng));

function GoogleMapComponent({
  center,
  zoom,
  multipleMarkers,
  customStyles,
  distance,
}) {
  const [activeMarker, setActiveMarker] = useState(null);
  const [hoverDistanceMeters, setHoverDistanceMeters] = useState(null);

  const {isLoaded, loadError} = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAP_API_KEY ||
      'AIzaSyAM1hg9be5WiIXnupnuM5BBicdXnMDaxRw',
    libraries: ['geometry'],
  });

  // Anchor = the first entry in multipleMarkers (the submitted/base address).
  // Callers should always place the anchor first; we fall back to `center`
  // so single-marker maps keep working.
  const anchorMarker = useMemo(
    () =>
      Array.isArray(multipleMarkers) && multipleMarkers.length > 0
        ? multipleMarkers[0]
        : null,
    [multipleMarkers],
  );

  const originLatLng = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const point = isValidLatLng(anchorMarker)
      ? anchorMarker
      : isValidLatLng(center)
      ? center
      : null;
    if (!point) return null;
    return new window.google.maps.LatLng(Number(point.lat), Number(point.lng));
  }, [anchorMarker, center, isLoaded]);

  const apiDistanceMeters = useMemo(() => {
    if (distance === null || distance === undefined || distance === '') {
      return null;
    }
    const n = typeof distance === 'number' ? distance : Number(distance);
    return Number.isFinite(n) ? n : null;
  }, [distance]);

  const handleActiveMarker = useCallback(
    marker => {
      if (marker === activeMarker) return setActiveMarker(null);
      setActiveMarker(marker);
    },
    [activeMarker],
  );

  const handleMarkerMouseOver = useCallback(
    marker => {
      if (!originLatLng || !window.google || !isValidLatLng(marker)) return;
      const markerLatLng = new window.google.maps.LatLng(
        Number(marker.lat),
        Number(marker.lng),
      );
      const meters = computeMeters(originLatLng, markerLatLng);
      setHoverDistanceMeters(Number.isFinite(meters) ? meters : null);
    },
    [originLatLng],
  );

  const handleMarkerMouseOut = useCallback(() => {
    setHoverDistanceMeters(null);
  }, []);

  useEffect(() => {
    if (multipleMarkers && multipleMarkers.length > 0) {
      setActiveMarker(multipleMarkers[0]);
    }
  }, [multipleMarkers]);

  // Reference distance used to colour the anchor radius. Prefer the API value
  // (already computed server-side), otherwise fall back to the farthest tracked
  // marker so the radius still reacts when no `distance` prop is supplied.
  const referenceDistanceMeters = useMemo(() => {
    if (apiDistanceMeters !== null) return apiDistanceMeters;
    if (
      !originLatLng ||
      !window.google ||
      !Array.isArray(multipleMarkers) ||
      multipleMarkers.length < 2
    ) {
      return 0;
    }
    let max = 0;
    for (let i = 1; i < multipleMarkers.length; i += 1) {
      const m = multipleMarkers[i];
      if (!isValidLatLng(m)) continue;
      const ll = new window.google.maps.LatLng(Number(m.lat), Number(m.lng));
      const d = computeMeters(originLatLng, ll);
      if (d != null && d > max) max = d;
    }
    return max;
  }, [apiDistanceMeters, originLatLng, multipleMarkers]);

  const radiusColors = referenceDistanceMeters > 50 ? '#ef6820' : '#306EEF';

  const displayedDistanceLabel = useMemo(() => {
    if (hoverDistanceMeters != null) {
      const label = formatMeters(hoverDistanceMeters);
      return label ? `${label}*` : null;
    }
    const apiLabel = formatMeters(apiDistanceMeters);
    return apiLabel ? `${apiLabel}*` : null;
  }, [hoverDistanceMeters, apiDistanceMeters]);
  const headerContent = useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = `
      <h3 style="margin: 0;
          font-size: 16px;
          font-weight: bold;
          color: #212121;"
      >
          ${activeMarker?.name}
      </h3>
          
    `;
    return div;
  }, [activeMarker]);
  const content = useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = `
      <p style="margin-top: 1px ; font-size: 14px; color: #757575;">
          Lat: <span style="font-weight: normal; color: #4E4E4E">${
            activeMarker?.lat
          }</span>
      </p>
      <p style=" font-size: 14px; color: #757575; margin-top: 6px">
        Lon: <span style="font-weight: normal; color: #4E4E4E">${
          activeMarker?.lng
        }</span>
      </p>
      <div style="height: 1px; width: 100%; background: #dedede; margin: 8px 0"></div>
      <a
        href="${`https://www.google.com/maps/search/?api=1&query=${activeMarker?.lat},${activeMarker?.lng}`}"
        target="_blank"
        rel="noopener noreferrer"
        style="color: #1a73e8; text-decoration: none; font-size: 14px;"
      >
        View on Google Maps
      </a>
          
    `;
    return div;
  }, [activeMarker]);

  const mapStyles = customStyles ?? containerStyle;

  const renderMap = () => (
    <div style={{position: 'relative', height: mapStyles.height}}>
      <GoogleMap
        mapContainerStyle={{...mapStyles, height: '100%'}}
        center={center}
        zoom={zoom ?? 14}
        clickableIcons
      >
        {originLatLng && (
          <CircleF
            center={originLatLng}
            radius={50}
            options={{
              strokeColor: radiusColors,
              strokeOpacity: 1,
              strokeWeight: 1,
              fillColor: radiusColors,
              fillOpacity: 0.15,
              clickable: false,
              draggable: false,
              editable: false,
              zIndex: 1,
            }}
          />
        )}

        {multipleMarkers ? (
          multipleMarkers.map((marker, i) => {
            if (!isValidLatLng(marker)) return null;
            const isAnchor = i === 0;
            const lat = Number(marker.lat);
            const lng = Number(marker.lng);
            return (
              <MarkerF
                key={`${lat}-${lng}-${i}`}
                onClick={() => handleActiveMarker(marker)}
                {...(!isAnchor && {
                  onMouseOver: () => handleMarkerMouseOver(marker),
                  onMouseOut: () => handleMarkerMouseOut(),
                })}
                title={marker?.name || 'No Name'}
                position={{lat, lng}}
                icon={{
                  anchor: new window.google.maps.Point(32, 32),
                  scaledSize: new window.google.maps.Size(34, 34),
                  url: marker?.markerImage,
                }}
              >
                {activeMarker?.lat === marker?.lat &&
                  activeMarker?.lng === marker?.lng && (
                    <InfoWindowF
                      anchor={marker}
                      position={{lat, lng}}
                      onCloseClick={() => setActiveMarker(null)}
                      options={{headerContent, content}}
                    >
                      <></>
                    </InfoWindowF>
                  )}
              </MarkerF>
            );
          })
        ) : (
          <MarkerF position={center} />
        )}
      </GoogleMap>
      {displayedDistanceLabel && (
        <div style={hoverDistanceStyle}>
          Distance from Anchor:
          <span style={{fontWeight: 700}}>{` ${displayedDistanceLabel}`}</span>
        </div>
      )}
    </div>
  );

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  return isLoaded ? renderMap() : <Loader height={35} />;
}

export default React.memo(GoogleMapComponent);
